import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormModal } from "@/components/form-modal";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { organisationUserRoles } from "@shared/schema";

const formSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
  organisationId: z.number().optional(),
  role: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface CreateUserModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  isGod?: boolean;
  ownerOrgIds?: number[];
  ownerOrgs?: { id: number; name: string }[];
}

async function createUser(data: FormData) {
  const payload: Record<string, unknown> = { username: data.username, password: data.password };
  if (data.organisationId) payload.organisationId = data.organisationId;
  if (data.role) payload.role = data.role;
  const response = await fetch('/api/users', {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to create user - its probably a duplicate username");
  }

  return response.json();
}

export function CreateUserModal({ isOpen, onOpenChange, isGod = false, ownerOrgIds = [], ownerOrgs = [] }: CreateUserModalProps) {
  const [showPassword, setShowPassword] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
      organisationId: undefined,
      role: "operator",
    },
  });

  async function assignUserToOrganisation({ organisationId, userId }: { organisationId: number; userId: number; }) {
    const response = await fetch(`/api/organisations/${organisationId}/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, role: "member" }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to assign user to organisation");
    }
    return response.json();
  }

  const mutation = useMutation({
    mutationFn: createUser,
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      toast({
        title: "User created",
        description: `User ${form.getValues('username')} has been created.`,
      });
      onOpenChange(false);
      form.reset();
      setShowPassword(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    if (!isGod && (!data.organisationId || ownerOrgs.length === 0)) {
      toast({ title: "Missing organisation", description: "Select an organisation.", variant: "destructive" });
      return;
    }
    mutation.mutate(data);
  };

  return (
    <FormModal
      open={isOpen}
      onOpenChange={onOpenChange}
      title="Create New User"
      form={form}
      onSubmit={onSubmit}
      isLoading={mutation.isPending}
      submitLabel={mutation.isPending ? "Creating..." : "Create User"}
      width="md"
    >
      <div className="text-sm text-muted-foreground mb-4">
        {isGod
          ? "Create a new user account. This user will not be assigned to any organisation."
          : "Create a new user and assign them to one of your organisations."}
      </div>
      
      <FormField
        control={form.control}
        name="username"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Username</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="password"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Password</FormLabel>
            <FormControl>
              <div className="relative">
                <Input
                  {...field}
                  type={showPassword ? "text" : "password"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  <span className="sr-only">{showPassword ? "Hide password" : "Show password"}</span>
                </Button>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {!isGod && ownerOrgs.length > 0 && (
        <>
          <FormField
            control={form.control}
            name="organisationId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organisation</FormLabel>
                <FormControl>
                  <Select 
                    value={field.value ? String(field.value) : ''} 
                    onValueChange={(val) => field.onChange(Number(val))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      {ownerOrgs.map(org => (
                        <SelectItem key={org.id} value={String(org.id)}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role</FormLabel>
                <FormControl>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operator">Operator</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </FormModal>
  );
} 